import React, { useState, useEffect, useRef, useCallback } from "react"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { submitPartnerForm } from "../../../store/Partner/PartnerAction"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../utils/mobileNumberUtils"
import heroImg from "../../../assets/partner/partner-hero.webp"
import "./heroPartner.scss"

const TYPING_PHRASES = ["PAN India", "450+ Cities", "Outstation Routes", "Local Trips", "Fleet Partners"]

const VEHICLE_TYPES = ["Hatchback", "Sedan", "SUV", "Innova / MUV"]
const CITIES = [
  "Delhi NCR", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata",
  "Pune", "Ahmedabad", "Surat", "Jaipur", "Lucknow", "Chandigarh",
  "Goa", "Indore", "Bhopal", "Nagpur", "Vadodara", "Rajkot",
  "Kochi", "Coimbatore", "Madurai", "Visakhapatnam", "Vijayawada",
  "Mysuru", "Mangaluru", "Hubli", "Nashik", "Aurangabad",
  "Agra", "Varanasi", "Prayagraj", "Kanpur", "Meerut", "Gorakhpur",
  "Noida", "Gurugram", "Faridabad", "Ghaziabad",
  "Udaipur", "Jodhpur", "Kota"
]

const HeroPartner = () => {
  const dispatch = useDispatch()
  const [partnerType, setPartnerType] = useState("individual")
  const [isSuccess, setIsSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const [displayText, setDisplayText] = useState("")
  const phraseIdx = useRef(0)
  const charIdx = useRef(0)
  const isDeleting = useRef(false)

  const tick = useCallback(() => {
    const current = TYPING_PHRASES[phraseIdx.current]
    if (!isDeleting.current) {
      charIdx.current++
      setDisplayText(current.slice(0, charIdx.current))
      if (charIdx.current === current.length) {
        isDeleting.current = true
        return 1800
      }
      return 100
    } else {
      charIdx.current--
      setDisplayText(current.slice(0, charIdx.current))
      if (charIdx.current === 0) {
        isDeleting.current = false
        phraseIdx.current = (phraseIdx.current + 1) % TYPING_PHRASES.length
        return 400
      }
      return 50
    }
  }, [])

  useEffect(() => {
    let id
    const run = () => {
      const delay = tick()
      id = setTimeout(run, delay)
    }
    run()
    return () => clearTimeout(id)
  }, [tick])

  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    city: "",
    vehicleType: "",
    companyName: "",
    numberOfVehicles: "",
    vehicleTypes: [],
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "mobile") {
      setFormData((prev) => ({ ...prev, mobile: parseMobileInput(value) }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleVehicleTypeToggle = (type) => {
    setFormData((prev) => {
      const types = prev.vehicleTypes.includes(type)
        ? prev.vehicleTypes.filter((t) => t !== type)
        : [...prev.vehicleTypes, type]
      return { ...prev, vehicleTypes: types }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.fullName.trim()) return toast.error("Please enter your full name")
    if (!isValidMobileNumber(formData.mobile)) return toast.error("Enter a valid 10-digit mobile number")
    if (!formData.email.trim() || !formData.email.includes("@")) return toast.error("Enter a valid email")
    if (!formData.city) return toast.error("Please select a city")

    if (partnerType === "individual" && !formData.vehicleType) {
      return toast.error("Please select a vehicle type")
    }
    if (partnerType === "fleet" && !formData.companyName.trim()) {
      return toast.error("Please enter your company name")
    }
    if (partnerType === "fleet" && (!formData.numberOfVehicles || Number(formData.numberOfVehicles) < 1)) {
      return toast.error("Please enter number of vehicles")
    }

    const submitData = {
      name: formData.fullName,
      mobile_number: formData.mobile,
      email: formData.email,
      city: formData.city,
      partner_type: partnerType,
      ...(partnerType === "individual"
        ? { vehicle_types: formData.vehicleType }
        : {
            company_name: formData.companyName,
            number_of_vehicles: formData.numberOfVehicles,
            vehicle_types: formData.vehicleTypes.join(", "),
          }),
    }

    setLoading(true)
    try {
      await dispatch(submitPartnerForm(submitData)).unwrap()
      setIsSuccess(true)
    } catch (error) {
      toast.error(error || "Something went wrong. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setIsSuccess(false)
    setFormData({
      fullName: "", mobile: "", email: "", city: "",
      vehicleType: "", companyName: "", numberOfVehicles: "", vehicleTypes: [],
    })
  }

  return (
    <section className="hero-partner" style={{ backgroundImage: `url(${heroImg})` }}>
      <div className="hero-partner__overlay" />
      <div className="hero-partner__container">
        <div className="hero-partner__left">
          <h1 className="hero-partner__title">
            Drive &amp; Earn with <span>Pulpit</span>
            <br />
            <span className="hero-partner__typing">{displayText}<span className="hero-partner__cursor">|</span></span>
          </h1>
          <p className="hero-partner__subtitle">
            Attach your car or fleet for outstation and local cab bookings.
            Join India's fastest-growing cab platform - individual drivers
            and fleet owners welcome across 450+ cities.
          </p>
          <div className="hero-partner__stats">
            <div className="hero-partner__stat">
              <span className="hero-partner__stat-number">10K+</span>
              <span className="hero-partner__stat-label">Rides Daily</span>
            </div>
            <div className="hero-partner__stat">
              <span className="hero-partner__stat-number">450+</span>
              <span className="hero-partner__stat-label">Cities</span>
            </div>
            <div className="hero-partner__stat">
              <span className="hero-partner__stat-number">4.9/5</span>
              <span className="hero-partner__stat-label">Rating</span>
            </div>
          </div>
        </div>

        <div className="hero-partner__right">
          {isSuccess ? (
            <div className="hero-partner__form-card hero-partner__form-card--success">
              <div className="hero-partner__success-icon">&#10003;</div>
              <h3 className="hero-partner__success-title">Thank You!</h3>
              <p className="hero-partner__success-text">
                Your application has been received. Our team will contact you within 24 hours.
              </p>
              <button className="hero-partner__success-btn" onClick={resetForm}>
                Submit another application
              </button>
            </div>
          ) : (
            <div className="hero-partner__form-card">
              <h2 className="hero-partner__form-title">Register as Cab Driver</h2>
              <p className="hero-partner__form-subtitle">Free registration - start earning in 24 hours</p>

              <div className="hero-partner__toggle">
                <button
                  type="button"
                  className={`hero-partner__toggle-btn${partnerType === "individual" ? " hero-partner__toggle-btn--active" : ""}`}
                  onClick={() => setPartnerType("individual")}
                >
                  Individual Driver
                </button>
                <button
                  type="button"
                  className={`hero-partner__toggle-btn${partnerType === "fleet" ? " hero-partner__toggle-btn--active" : ""}`}
                  onClick={() => setPartnerType("fleet")}
                >
                  Fleet Owner
                </button>
              </div>

              <form onSubmit={handleSubmit} className="hero-partner__form">
                <input
                  type="text"
                  name="fullName"
                  className="hero-partner__input"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />

                <input
                  type="tel"
                  name="mobile"
                  className={`hero-partner__input${isValidMobileNumber(formData.mobile) ? " hero-partner__input--valid" : formData.mobile.length > 0 ? " hero-partner__input--invalid" : ""}`}
                  placeholder="Mobile Number *"
                  value={formatMobileDisplay(formData.mobile)}
                  onChange={handleChange}
                  maxLength={11}
                  required
                />

                <input
                  type="email"
                  name="email"
                  className="hero-partner__input"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                {partnerType === "fleet" && (
                  <input
                    type="text"
                    name="companyName"
                    className="hero-partner__input"
                    placeholder="Company / Agency Name *"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                )}

                <select
                  name="city"
                  className={`hero-partner__input hero-partner__select${!formData.city ? " hero-partner__select--placeholder" : ""}`}
                  value={formData.city}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Select City *</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                {partnerType === "individual" ? (
                  <select
                    name="vehicleType"
                    className={`hero-partner__input hero-partner__select${!formData.vehicleType ? " hero-partner__select--placeholder" : ""}`}
                    value={formData.vehicleType}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>Vehicle Type *</option>
                    {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : (
                  <>
                    <input
                      type="number"
                      name="numberOfVehicles"
                      className="hero-partner__input"
                      placeholder="Number of Vehicles *"
                      value={formData.numberOfVehicles}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                    <div className="hero-partner__checkbox-group">
                      <span className="hero-partner__checkbox-label">Vehicle Types:</span>
                      <div className="hero-partner__checkboxes">
                        {VEHICLE_TYPES.map((v) => (
                          <label key={v} className="hero-partner__checkbox">
                            <input
                              type="checkbox"
                              checked={formData.vehicleTypes.includes(v)}
                              onChange={() => handleVehicleTypeToggle(v)}
                            />
                            <span>{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" className="hero-partner__submit" disabled={loading}>
                  {loading ? "Submitting..." : "Register as Partner"}
                </button>

                <p className="hero-partner__form-footer">
                  By registering, you agree to our terms &amp; privacy policy.
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default HeroPartner
